import React, { useState, useContext } from 'react';
import api from '../api';
import { UserContext } from '../UserContext';
import { useTranslation } from '../LanguageContext';
import SubscribePanel from './SubscribePanel';
import { PaymentDone, StripeIntentForm, StripeSheet } from './StripePayment';

function Pay() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [paying, setPaying] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post('/api/check-promo/', { code });
      if (res.status === 200) {
        window.location.reload();
      }

    } catch (error) {
      alert(t('payments.promoInvalid'));
    }
  }

  return (
    <div className="space-y-4">
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">{t('payments.subscribeTitle')}</h3>
        <p className="text-sm text-slate-500 mb-4">{t('payments.subscribeDesc')}</p>
        {paying ? (
          <SubscribePanel
            target="platform"
            onSubscribed={() => setTimeout(() => window.location.reload(), 1200)}
            onCancel={() => setPaying(false)}
          />
        ) : (
          <button type="button" className="btn-primary w-full" onClick={() => setPaying(true)}>
            {t('payments.payNowBtn')}
          </button>
        )}
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">{t('payments.promoTitle')}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            placeholder={t('payments.promoPlaceholder')}
            className="input"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={511}
            required
          />
          <button type="submit" className="btn-success w-full">
            {t('payments.applyCodeBtn')}
          </button>
        </form>
      </div>
    </div>
  )
}

/**
 * Managing a live subscription, without the Stripe billing portal.
 *
 * The portal was one button that left the site to do three things. Cancelling
 * and resuming already had an endpoint of our own (`/api/manage-subscription/`),
 * and replacing the card is a Setup Intent through the same Payment Element as
 * everything else — so all three happen here now.
 */
function Manage() {
  const { t } = useTranslation();
  const { user, refreshUser } = useContext(UserContext);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [cardSetup, setCardSetup] = useState(null);   // { client_secret, … }
  const [cardDone, setCardDone] = useState(false);

  const subscription = user.subscription || {};
  const willCancel = subscription.cancel_at_period_end === true;
  // Finance mounts this for anyone off trial, including someone with no
  // subscription at all — there is nothing to cancel or resume for them, and a
  // button that posts an undefined id is worse than no button.
  const manageable = Boolean(subscription.source_id) && subscription.status === 'active';

  const manage = async (action) => {
    if (busy) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const res = await api.post('/api/manage-subscription/', {
        action,
        sub_id: subscription.source_id,
      });
      setNotice(res.data?.message || '');
      refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || t('payments.errorPortal'));
    }
    setBusy(false);
    setConfirming(false);
  };

  const startCardUpdate = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await api.post('/api/billing/payment-method/', {});
      setCardSetup(res.data);
    } catch (err) {
      setError(t('billing.startError'));
    }
    setBusy(false);
  };

  const finishCardUpdate = async (intent) => {
    try {
      await api.put('/api/billing/payment-method/', { setup_intent_id: intent.id });
    } catch (err) {
      return t('billing.cardUpdateFailed');
    }
    setCardSetup(null);
    setCardDone(true);
    return null;
  };

  if (user.subscription.promocode_used) {
    return (
      <div className="card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-slate-700 font-medium">{t('payments.lifetimePromo')}</p>
        </div>
      </div>
    )
  }
  else {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">{t('payments.manageSubTitle')}</h3>

        {willCancel && manageable && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3 leading-relaxed">
            {t('payments.willCancelNote')}
          </p>
        )}

        <div className="space-y-2">
          <button className="btn-secondary w-full flex items-center justify-center gap-2" type="button"
            onClick={startCardUpdate} disabled={busy}>
            {t('payments.updateCardBtn')}
          </button>

          {!manageable ? null : willCancel ? (
            <button className="btn-success w-full flex items-center justify-center gap-2" type="button"
              onClick={() => manage('reactivate')} disabled={busy}>
              {busy && <span className="spinner-sm" />}
              {t('payments.reactivateBtn')}
            </button>
          ) : confirming ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 space-y-2">
              <p className="text-xs text-red-700 leading-relaxed">{t('payments.cancelConfirm')}</p>
              <div className="flex gap-2">
                <button className="btn-secondary flex-1 py-2 text-sm" type="button"
                  onClick={() => setConfirming(false)} disabled={busy}>
                  {t('billing.cancel')}
                </button>
                <button className="flex-1 py-2 text-sm font-bold rounded-xl bg-red-600 text-white hover:bg-red-700 transition flex items-center justify-center gap-2"
                  type="button" onClick={() => manage('cancel')} disabled={busy}>
                  {busy && <span className="spinner-sm" />}
                  {t('payments.cancelSubConfirmBtn')}
                </button>
              </div>
            </div>
          ) : (
            <button className="w-full py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl transition"
              type="button" onClick={() => setConfirming(true)} disabled={busy}>
              {t('payments.cancelSubBtn')}
            </button>
          )}
        </div>

        {notice && <p className="text-xs text-emerald-700 mt-3">{notice}</p>}
        {error && <p className="text-xs text-red-600 mt-3">{error}</p>}

        {/* Replacing the card: the same Payment Element, in setup mode. */}
        {cardSetup && (
          <StripeSheet title={t('payments.updateCardTitle')}
            subtitle={t('payments.updateCardHint')}
            onClose={() => setCardSetup(null)}>
            <StripeIntentForm
              mode="setup"
              clientSecret={cardSetup.client_secret}
              publishableKey={cardSetup.publishable_key}
              submitLabel={t('payments.updateCardBtn')}
              onSucceeded={finishCardUpdate}
              onCancel={() => setCardSetup(null)}
              note={t('billing.securityNote')}
            />
          </StripeSheet>
        )}

        {cardDone && (
          <StripeSheet title={t('payments.updateCardTitle')} onClose={() => setCardDone(false)}>
            <PaymentDone title={t('payments.cardUpdated')}
              detail={t('payments.cardUpdatedDetail')}
              onClose={() => setCardDone(false)} />
          </StripeSheet>
        )}
      </div>
    )
  }
}

function SubscriptionInfo() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const formated_date = (date) => { return date.match(/^\d{4}-\d{2}-\d{2}/)[0]; }

  return (
    <div className="card mt-4">
      <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('payments.subDetailsTitle')}</h3>
      <div className="divide-y divide-slate-100">
        <div className="flex justify-between py-2.5">
          <span className="text-sm text-slate-500">{t('payments.startedAt')}</span>
          <span className="text-sm font-medium text-slate-700">{user.subscription.started_at ? formated_date(user.subscription.started_at) : "---"}</span>
        </div>
        <div className="flex justify-between py-2.5">
          <span className="text-sm text-slate-500">{t('payments.cancelAt')}</span>
          <span className="text-sm font-medium text-slate-700">{user.subscription.cancel_at ? formated_date(user.subscription.cancel_at) : "---"}</span>
        </div>
        <div className="flex justify-between py-2.5">
          <span className="text-sm text-slate-500">{t('payments.canceledAt')}</span>
          <span className="text-sm font-medium text-slate-700">{user.subscription.canceled_at ? formated_date(user.subscription.canceled_at) : "---"}</span>
        </div>
        <div className="flex justify-between py-2.5">
          <span className="text-sm text-slate-500">{t('payments.status')}</span>
          <span className={`text-sm font-semibold px-2.5 py-0.5 rounded-full ${user.subscription.status === "active"
            ? "bg-emerald-100 text-emerald-700"
            : "bg-red-100 text-red-700"
            }`}>
            {user.subscription.status ? user.subscription.status : "---"}
          </span>
        </div>
      </div>
    </div>
  )
}

const STAR_MAX = 10000;

// One star, one dollar. This has to agree with `STAR_PAYOUT_USD` in the backend
// settings *and* with the unit amount on the `STAR_UNIT` Stripe price — the
// quantity is what gets multiplied at checkout, so a mismatch here quotes a
// total the payment page then contradicts.
const STAR_USD = 1;

/**
 * Star top-up. Deliberately headerless: the only caller is the star wallet
 * card, which already shows the title and the balance — repeating either here
 * just says the same number twice.
 */
function BuyStars() {
  const { t } = useTranslation();
  const { refreshUser } = useContext(UserContext);
  const [amount, setAmount] = useState("10");
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState(null);      // { client_secret, … }
  const [bought, setBought] = useState(0);
  const [error, setError] = useState('');

  const quantity = Math.min(STAR_MAX, Math.max(0, parseInt(amount, 10) || 0));

  // Eski sabit paketler — yerini serbest miktar girişi aldı.
  // const starOptions = [
  //   { amount: 10, price: "€8.99" },
  //   { amount: 25, price: "€19.99", popular: true },
  //   { amount: 50, price: "€34.99" },
  //   { amount: 100, price: "€59.99", bestValue: true },
  // ];

  const handleBuyStars = async () => {
    if (quantity < 1 || loading) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/billing/stars/', { amount: quantity });
      if (res.data?.client_secret) {
        setIntent(res.data);
      } else {
        setError(t('billing.startError'));
      }
    } catch (err) {
      setError(err.response?.data?.error === 'invalid_amount'
        ? t('billing.invalidAmount') : t('billing.startError'));
    }
    setLoading(false);
  }

  /** Card cleared: credit the stars now rather than waiting for the webhook. */
  const settle = async (paymentIntent) => {
    try {
      await api.post('/api/billing/purchase/confirm/', {
        payment_intent_id: paymentIntent.id,
      });
    } catch (err) {
      // The webhook still credits them; the balance is just a moment behind.
    }
    setBought(intent.quantity);
    setIntent(null);
    refreshUser();
    // A second read a few seconds later, in case the webhook was the one that
    // actually moved the balance.
    setTimeout(refreshUser, 3000);
    return null;
  }

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <label htmlFor="star-amount" className="block text-xs font-semibold text-slate-500">
          {t('payments.starAmountLabel')}
        </label>
        <span className="text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">
          {t('payments.starRate')}
        </span>
      </div>

      {/* Stepper and the pay button share one row; the button drops to its
          own line only when the viewport is too narrow to hold both. */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setAmount(String(Math.max(1, quantity - 1)))}
          className="w-10 h-10 flex-shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-lg flex items-center justify-center transition"
          aria-label="-"
        >−</button>

        <div className="relative flex-1 min-w-[92px]">
          <input
            id="star-amount"
            type="number"
            inputMode="numeric"
            min="1"
            max={STAR_MAX}
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
            onBlur={() => setAmount(String(Math.max(1, quantity)))}
            className="w-full text-center text-2xl font-black text-slate-800 border border-slate-200 rounded-xl py-2.5 pr-9 focus:outline-none focus:border-[#20c9b5] focus:ring-2 focus:ring-[#20c9b5]/20 transition [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <img src="/star192.png" alt="star" className="w-5 h-5 object-contain absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-80" />
        </div>

        <button
          type="button"
          onClick={() => setAmount(String(Math.min(STAR_MAX, quantity + 1)))}
          className="w-10 h-10 flex-shrink-0 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-lg flex items-center justify-center transition"
          aria-label="+"
        >+</button>

        <button
          type="button"
          onClick={handleBuyStars}
          disabled={quantity < 1 || loading}
          className="flex-1 min-w-[132px] h-11 px-4 rounded-xl bg-[#20c9b5] text-white font-bold text-sm shadow-sm hover:bg-[#17a898] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? <span className="spinner-sm" /> : t('wallet.continueToPayment')}
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 mt-2.5 pt-2.5 border-t border-slate-100">
        <span className="text-xs font-semibold text-slate-500">{t('payments.starTotal')}</span>
        <span className="text-base font-black text-slate-800 tabular-nums">
          ${(quantity * STAR_USD).toLocaleString('en-US')}
        </span>
      </div>

      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2 mt-2">
          {error}
        </p>
      )}

      <p className="text-[11px] text-slate-400 mt-2">{t('payments.starCheckoutNote')}</p>

      {/* The card, in a sheet over the star card that opened it. */}
      {intent && (
        <StripeSheet
          title={t('payments.buyStarsTitle')}
          subtitle={t('payments.starSheetHint').replace('{count}', intent.quantity)}
          onClose={() => setIntent(null)}
        >
          <StripeIntentForm
            clientSecret={intent.client_secret}
            publishableKey={intent.publishable_key}
            amountUsd={intent.amount_usd}
            submitLabel={t('billing.payNow')}
            onSucceeded={settle}
            onCancel={() => setIntent(null)}
          />
        </StripeSheet>
      )}

      {bought > 0 && (
        <StripeSheet title={t('payments.buyStarsTitle')} onClose={() => setBought(0)}>
          <PaymentDone
            title={t('payments.starsAdded').replace('{count}', bought)}
            detail={t('payments.starsAddedDetail')}
            onClose={() => setBought(0)}
          />
        </StripeSheet>
      )}
    </div>
  );
}

function Payments() {
  const { user } = useContext(UserContext);

  return (
    <div className="w-full mt-4 mb-8">
      {user.subscription.status === "active" ?
        <Manage />
        :
        <Pay />
      }
      {user.subscription.started_at ? <SubscriptionInfo /> : null}
      <div className="card mt-6 border-2 border-[#20c9b5]/20">
        <BuyStars />
      </div>
    </div>
  )
}
export default Payments;
export { BuyStars, Manage, Pay, SubscriptionInfo };

